"use strict";

const moment = require('moment-timezone');
const ONLY_DATE_FORMAT = 'YYYYMMDD';
const ONLY_TIME_FORMAT = 'HHmm';

/**
 * Creates a boolean function to check a rule and return true if it is expired,
 * defined as: has a date range, and its end date has already passed
 *
 * @param {moment.Moment} currentMoment
 * @returns function
 */
const getDateRangeExpiredCheckerFromMoment = currentMoment => {
  const dateNumber = parseInt(currentMoment.format(ONLY_DATE_FORMAT));
  return rule => {
    if (rule.type === 'date_range') {
      const {
        dateEnd
      } = rule.metadata;
      return !!dateEnd && dateEnd < dateNumber;
    } else {
      return false;
    }
  };
};

/**
 * Creates a boolean function to validate a time based rule at a moment in time
 * which returns true when a rule is valid, false otherwise
 *
 * @param {moment.Moment} currentMoment
 * @returns function
 */
const getTimeBasedRuleValidatorFromMoment = currentMoment => rule => {
  switch (rule.type) {
    case 'date_range':
      {
        const {
          dateEnd,
          dateStart
        } = rule.metadata;
        const currentDate = parseInt(currentMoment.format(ONLY_DATE_FORMAT), 10);
        return (!dateStart || dateStart <= currentDate) && (!dateEnd || currentDate <= dateEnd);
      }
    case 'time_range':
      {
        const {
          timeEnd,
          timeStart
        } = rule.metadata;
        if (!timeEnd && !timeStart) return true; // TODO: why not the same check on date_range for an early return

        const currentTime = parseInt(currentMoment.format(ONLY_TIME_FORMAT), 10);
        if (!timeEnd) return timeStart <= currentTime;
        if (!timeStart) return currentTime <= timeEnd;
        if (timeStart < timeEnd) {
          return timeStart <= currentTime && currentTime <= timeEnd;
        } else {
          return timeStart <= currentTime || currentTime <= timeEnd;
        }
      }
    case 'week_days':
      {
        const {
          days
        } = rule.metadata;
        const currentDayName = currentMoment.format('dddd').toLowerCase();
        return days.includes(currentDayName);
      }
    default:
      // TODO: what is the desired behavior here
      throw new Error('NOT IMPLEMENTED');
  }
};

/**
 * Creates a boolean function to validate a time based rule at a given datetime
 * and timezone, which returns true when a rule is valid, false otherwise
 *
 * @param {date|String} currentDate
 * @param {String} tz
 * @returns function
 */
const getTimeBasedRuleValidatorFromDateAndTz = (currentDate, tz) => {
  const currentMoment = moment(currentDate).tz(tz);
  return getTimeBasedRuleValidatorFromMoment(currentMoment);
};

/**
 * Creates a boolean function to validate a list of time based rules at  given moment
 * returning true when all rules are valid, false otherwise
 *
 * @param {moment.Moment} currentMoment
 * @returns function
 */
const getTimeBasedRuleListValidatorFromMoment = currentMoment => {
  const timeBasedRuleValidator = getTimeBasedRuleValidatorFromMoment(currentMoment);
  return rules => rules.every(timeBasedRuleValidator);
};

/**
 * Creates a boolean function to validate a list of time based rules at given datetime
 * and timezone, which returns true when all rules are valid, false otherwise
 *
 * @param {date|String} currentDate
 * @param {String} tz
 * @returns function
 */
const getTimeBasedRuleListValidatorFromDateAndTz = (currentDate, tz) => {
  const currentMoment = moment(currentDate).tz(tz);
  return getTimeBasedRuleListValidatorFromMoment(currentMoment);
};
module.exports = {
  getDateRangeExpiredCheckerFromMoment,
  getTimeBasedRuleValidatorFromMoment,
  getTimeBasedRuleValidatorFromDateAndTz,
  getTimeBasedRuleListValidatorFromMoment,
  getTimeBasedRuleListValidatorFromDateAndTz
};